import { useRef, useState } from 'react';
import { Plus, Trash2, MousePointerClick, Calculator } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { ColumnList } from './DisplayOptions';
import { compileFormula } from './formulaEngine';
import {
  ActionColumnDef,
  ColumnDef,
  ColumnId,
  CustomColumnDef,
  DateFilterState,
  FilterState,
  FormatsState,
  FormulaColumnDef,
  SortState,
} from './types';

interface ColumnManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columns: ColumnDef<any>[];
  hiddenColumns: Set<ColumnId>;
  filters: FilterState;
  dateFilters: DateFilterState;
  sort: SortState;
  formats: FormatsState;
  groupBy: ColumnId | undefined;
  customColumns: CustomColumnDef[];
  onToggle: (id: ColumnId) => void;
  onMove: (draggedId: ColumnId, targetId: ColumnId | null) => void;
  onAddCustomColumn: (def: CustomColumnDef) => void;
  onRemoveCustomColumn: (id: ColumnId) => void;
}

type AddForm = null | 'action' | 'formula';

/**
 * Insertable functions + operators for the calculated-column builder, grouped
 * for the "Insert function…" dropdown. `snippet` is what gets injected at the
 * caret; when it contains "(", the caret lands inside the parens.
 */
const FUNCTION_GROUPS: { label: string; items: { label: string; snippet: string }[] }[] = [
  {
    label: 'Dates',
    items: [
      { label: 'DAYS_AGO(date)', snippet: 'DAYS_AGO()' },
      { label: 'DAYS_FROM_NOW(date)', snippet: 'DAYS_FROM_NOW()' },
      { label: 'DAYS_BETWEEN(a, b)', snippet: 'DAYS_BETWEEN(, )' },
      { label: 'TODAY()', snippet: 'TODAY()' },
      { label: 'NOW()', snippet: 'NOW()' },
      { label: 'DATE("2026-01-01")', snippet: 'DATE("")' },
      { label: 'DATEADD(date, days)', snippet: 'DATEADD(, )' },
      { label: 'YEAR(date)', snippet: 'YEAR()' },
      { label: 'MONTH(date)', snippet: 'MONTH()' },
      { label: 'DAY(date)', snippet: 'DAY()' },
      { label: 'WEEKDAY(date)', snippet: 'WEEKDAY()' },
      { label: 'FORMATDATE(date, "YYYY-MM-DD")', snippet: 'FORMATDATE(, "YYYY-MM-DD")' },
    ],
  },
  {
    label: 'Math',
    items: [
      { label: 'ROUND(x, decimals)', snippet: 'ROUND(, )' },
      { label: 'ABS(x)', snippet: 'ABS()' },
      { label: 'FLOOR(x)', snippet: 'FLOOR()' },
      { label: 'CEIL(x)', snippet: 'CEIL()' },
      { label: 'MIN(a, b, …)', snippet: 'MIN(, )' },
      { label: 'MAX(a, b, …)', snippet: 'MAX(, )' },
      { label: 'SUM(a, b, …)', snippet: 'SUM(, )' },
      { label: 'AVG(a, b, …)', snippet: 'AVG(, )' },
      { label: 'MOD(a, b)', snippet: 'MOD(, )' },
      { label: 'POW(x, y)', snippet: 'POW(, )' },
      { label: 'SQRT(x)', snippet: 'SQRT()' },
    ],
  },
  {
    label: 'Logic',
    items: [
      { label: 'IF(cond, a, b)', snippet: 'IF(, , )' },
      { label: 'AND(a, b, …)', snippet: 'AND(, )' },
      { label: 'OR(a, b, …)', snippet: 'OR(, )' },
      { label: 'NOT(x)', snippet: 'NOT()' },
      { label: 'COALESCE(a, b, …)', snippet: 'COALESCE(, )' },
      { label: 'ISBLANK(x)', snippet: 'ISBLANK()' },
    ],
  },
  {
    label: 'Text',
    items: [
      { label: 'CONCAT(a, b, …)', snippet: 'CONCAT(, )' },
      { label: 'UPPER(s)', snippet: 'UPPER()' },
      { label: 'LOWER(s)', snippet: 'LOWER()' },
      { label: 'TRIM(s)', snippet: 'TRIM()' },
      { label: 'LEN(s)', snippet: 'LEN()' },
      { label: 'LEFT(s, n)', snippet: 'LEFT(, )' },
      { label: 'RIGHT(s, n)', snippet: 'RIGHT(, )' },
      { label: 'MID(s, start, len)', snippet: 'MID(, , )' },
      { label: 'CONTAINS(s, sub)', snippet: 'CONTAINS(, )' },
      { label: 'REPLACE(s, find, repl)', snippet: 'REPLACE(, , )' },
    ],
  },
  {
    label: 'Operators',
    items: [
      { label: '+  (add)', snippet: ' + ' },
      { label: '−  (subtract)', snippet: ' - ' },
      { label: '×  (multiply)', snippet: ' * ' },
      { label: '÷  (divide)', snippet: ' / ' },
      { label: '^  (power)', snippet: ' ^ ' },
      { label: '&  (join text)', snippet: ' & ' },
      { label: '=  (equals)', snippet: ' = ' },
      { label: '!=  (not equal)', snippet: ' != ' },
      { label: '>  (greater than)', snippet: ' > ' },
      { label: '<  (less than)', snippet: ' < ' },
      { label: '>=  (at least)', snippet: ' >= ' },
      { label: '<=  (at most)', snippet: ' <= ' },
    ],
  },
];

/**
 * Full column management surface. Shows every registry column (visible or
 * hidden) with checkboxes + drag-reorder, plus user-defined custom columns
 * (action / formula) with delete + an inline add form.
 */
export function ColumnManagerDialog({
  open,
  onOpenChange,
  columns,
  hiddenColumns,
  filters,
  dateFilters,
  sort,
  formats,
  groupBy,
  customColumns,
  onToggle,
  onMove,
  onAddCustomColumn,
  onRemoveCustomColumn,
}: ColumnManagerDialogProps) {
  const [addForm, setAddForm] = useState<AddForm>(null);

  const basicCols = columns.filter((c) => (c.category ?? 'basic') === 'basic');
  const relatedCols = columns.filter((c) => c.category === 'related');
  const configCols = columns.filter((c) => c.category === 'config');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-sm">
        <DialogHeader>
          <DialogTitle className="text-base">Manage columns</DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto -mx-2">
          <div className="px-2">
            <SectionHeader>Custom columns</SectionHeader>
            {customColumns.length === 0 && addForm === null && (
              <div className="px-2 py-1 text-[12px] text-stone-500">
                No custom columns yet.
              </div>
            )}
            {customColumns.map((def) => (
              <CustomColumnRow
                key={def.id}
                def={def}
                onRemove={() => onRemoveCustomColumn(def.id)}
              />
            ))}

            {addForm === 'action' && (
              <AddActionColumnForm
                columns={columns}
                onCancel={() => setAddForm(null)}
                onSubmit={(def) => {
                  onAddCustomColumn(def);
                  setAddForm(null);
                }}
              />
            )}
            {addForm === 'formula' && (
              <AddFormulaColumnForm
                columns={columns}
                onCancel={() => setAddForm(null)}
                onSubmit={(def) => {
                  onAddCustomColumn(def);
                  setAddForm(null);
                }}
              />
            )}
            {addForm === null && (
              <div className="mt-1 flex flex-col gap-0.5">
                <button
                  onClick={() => setAddForm('formula')}
                  className="w-full flex items-center gap-2 px-3 h-8 rounded-sm text-[13px] font-medium text-[#0078d4] hover:bg-[#0078d4]/10 text-left transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Add calculated column
                </button>
                <button
                  onClick={() => setAddForm('action')}
                  className="w-full flex items-center gap-2 px-3 h-8 rounded-sm text-[13px] font-medium text-[#0078d4] hover:bg-[#0078d4]/10 text-left transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add action column
                </button>
              </div>
            )}

            <SectionHeader>Columns</SectionHeader>
            <ColumnList
              cols={basicCols}
              hiddenColumns={hiddenColumns}
              filters={filters}
              dateFilters={dateFilters}
              sort={sort}
              formats={formats}
              groupBy={groupBy}
              onToggle={onToggle}
              onMove={onMove}
            />
            {relatedCols.length > 0 && (
              <>
                <SectionHeader>Related</SectionHeader>
                <ColumnList
                  cols={relatedCols}
                  hiddenColumns={hiddenColumns}
                  filters={filters}
                  dateFilters={dateFilters}
                  sort={sort}
                  formats={formats}
                  groupBy={groupBy}
                  onToggle={onToggle}
                  onMove={onMove}
                />
              </>
            )}
            {configCols.length > 0 && (
              <>
                <SectionHeader>Screen Config</SectionHeader>
                <ColumnList
                  cols={configCols}
                  hiddenColumns={hiddenColumns}
                  filters={filters}
                  dateFilters={dateFilters}
                  sort={sort}
                  formats={formats}
                  groupBy={groupBy}
                  onToggle={onToggle}
                  onMove={onMove}
                />
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-stone-200 -mx-6 px-6">
          <Button
            onClick={() => onOpenChange(false)}
            className="bg-[#0078d4] hover:bg-[#106ebe] text-white rounded-sm"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2 pt-3 pb-1 text-[11px] font-semibold text-stone-500">
      {children}
    </div>
  );
}

function CustomColumnRow({
  def,
  onRemove,
}: {
  def: CustomColumnDef;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-stone-100 group">
      {def.type === 'formula' ? (
        <Calculator className="w-3.5 h-3.5 text-stone-500 shrink-0" />
      ) : (
        <MousePointerClick className="w-3.5 h-3.5 text-stone-500 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-stone-900 truncate">{def.label}</div>
        <div className="text-[11px] text-stone-500 truncate font-mono">
          {def.type === 'action'
            ? `Set ${def.targetField} to "${def.setValue}"`
            : def.expression}
        </div>
      </div>
      <button
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500 p-1 transition-opacity"
        title="Remove column"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

interface AddActionColumnFormProps {
  columns: ColumnDef<any>[];
  onCancel: () => void;
  onSubmit: (def: ActionColumnDef) => void;
}

function AddActionColumnForm({ columns, onCancel, onSubmit }: AddActionColumnFormProps) {
  const [label, setLabel] = useState('');
  const [buttonLabel, setButtonLabel] = useState('');
  const [targetField, setTargetField] = useState(columns[0]?.id ?? '');
  const [setValue, setSetValue] = useState('true');

  const valid =
    label.trim().length > 0 &&
    buttonLabel.trim().length > 0 &&
    targetField.length > 0;

  const handleSubmit = () => {
    if (!valid) return;
    onSubmit({
      id: `custom_${Math.random().toString(36).slice(2, 10)}`,
      type: 'action',
      label: label.trim(),
      buttonLabel: buttonLabel.trim(),
      targetField,
      setValue,
    });
  };

  return (
    <div className="mt-1 p-2 rounded-sm border border-stone-200 bg-stone-50/60 space-y-2">
      <div className="text-[12px] font-semibold text-stone-700">New action column</div>
      <Field label="Column label">
        <Input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Mark complete"
          className="h-8 text-[13px] rounded-sm"
        />
      </Field>
      <Field label="Button text">
        <Input
          value={buttonLabel}
          onChange={(e) => setButtonLabel(e.target.value)}
          placeholder="Complete"
          className="h-8 text-[13px] rounded-sm"
        />
      </Field>
      <Field label="Target field">
        <select
          value={targetField}
          onChange={(e) => setTargetField(e.target.value)}
          className="w-full h-8 text-xs rounded-sm border border-stone-200 bg-white px-2 focus:border-[#0078d4] focus:outline-none transition-colors"
        >
          {columns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Set value to">
        <Input
          value={setValue}
          onChange={(e) => setSetValue(e.target.value)}
          placeholder="true"
          className="h-8 text-[13px] rounded-sm"
        />
      </Field>
      <div className="flex justify-end gap-2 pt-1">
        <Button
          variant="outline"
          onClick={onCancel}
          className="h-8 rounded-sm text-[13px]"
        >
          Cancel
        </Button>
        <Button
          disabled={!valid}
          onClick={handleSubmit}
          className="h-8 rounded-sm text-[13px] bg-[#0078d4] hover:bg-[#106ebe] text-white"
        >
          Add column
        </Button>
      </div>
    </div>
  );
}

interface AddFormulaColumnFormProps {
  columns: ColumnDef<any>[];
  onCancel: () => void;
  onSubmit: (def: FormulaColumnDef) => void;
}

function AddFormulaColumnForm({ columns, onCancel, onSubmit }: AddFormulaColumnFormProps) {
  const [label, setLabel] = useState('');
  const [expression, setExpression] = useState('');
  const [resultType, setResultType] = useState<'number' | 'text'>('number');
  const [decimals, setDecimals] = useState('');
  const exprRef = useRef<HTMLTextAreaElement>(null);

  // Live syntax check. Empty expression isn't an "error" to show in red — it
  // just disables the Add button.
  const compiled = expression.trim() ? compileFormula(expression) : null;
  const exprError = compiled && !compiled.ok ? compiled.error : null;
  const valid = label.trim().length > 0 && !!compiled?.ok;

  // Insert a snippet at the caret (or append) and keep focus. When the snippet
  // contains "(", the caret lands just inside it so the user can type the first
  // argument; otherwise it lands at the end of the inserted text.
  const insertSnippet = (snippet: string) => {
    const caretOffset = snippet.includes('(') ? snippet.indexOf('(') + 1 : snippet.length;
    const el = exprRef.current;
    if (!el) {
      setExpression((e) => e + snippet);
      return;
    }
    const start = el.selectionStart ?? expression.length;
    const end = el.selectionEnd ?? expression.length;
    const next = expression.slice(0, start) + snippet + expression.slice(end);
    setExpression(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + caretOffset;
      el.setSelectionRange(caret, caret);
    });
  };

  const handleSubmit = () => {
    if (!valid) return;
    const parsedDecimals = decimals.trim() === '' ? undefined : Math.max(0, Math.floor(Number(decimals)));
    onSubmit({
      id: `custom_${Math.random().toString(36).slice(2, 10)}`,
      type: 'formula',
      label: label.trim(),
      expression: expression.trim(),
      resultType,
      ...(resultType === 'number' && parsedDecimals !== undefined && !isNaN(parsedDecimals)
        ? { decimals: parsedDecimals }
        : {}),
    });
  };

  return (
    <div className="mt-1 p-2 rounded-sm border border-stone-200 bg-stone-50/60 space-y-2">
      <div className="text-[12px] font-semibold text-stone-700">New calculated column</div>
      <Field label="Column label">
        <Input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Days since order"
          className="h-8 text-[13px] rounded-sm"
        />
      </Field>
      <Field label="Formula">
        <textarea
          ref={exprRef}
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
          placeholder="DAYS_AGO([Date])"
          rows={2}
          spellCheck={false}
          className={cn(
            'w-full text-[13px] font-mono rounded-sm border bg-white px-2 py-1.5 resize-y focus:outline-none transition-colors',
            exprError
              ? 'border-red-300 focus:border-red-400'
              : 'border-stone-200 focus:border-[#0078d4]'
          )}
        />
      </Field>
      {exprError && <div className="text-[11px] text-red-500 -mt-1">{exprError}</div>}

      <div className="grid grid-cols-2 gap-2">
        <select
          value=""
          onChange={(e) => {
            if (e.target.value) insertSnippet(`[${e.target.value}]`);
          }}
          className="h-8 text-xs rounded-sm border border-stone-200 bg-white px-2 focus:border-[#0078d4] focus:outline-none transition-colors text-stone-600"
        >
          <option value="">Insert field…</option>
          {columns.map((c) => (
            <option key={c.id} value={c.label}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          value=""
          onChange={(e) => {
            if (e.target.value) insertSnippet(e.target.value);
          }}
          className="h-8 text-xs rounded-sm border border-stone-200 bg-white px-2 focus:border-[#0078d4] focus:outline-none transition-colors text-stone-600"
        >
          <option value="">Insert function…</option>
          {FUNCTION_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.items.map((item) => (
                <option key={item.label} value={item.snippet}>
                  {item.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <p className="text-[11px] text-stone-400">
        e.g. <span className="font-mono">IF(DAYS_AGO([Date]) &gt; 7, "Overdue", "OK")</span>
      </p>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Result type">
          <select
            value={resultType}
            onChange={(e) => setResultType(e.target.value as 'number' | 'text')}
            className="w-full h-8 text-xs rounded-sm border border-stone-200 bg-white px-2 focus:border-[#0078d4] focus:outline-none transition-colors"
          >
            <option value="number">Number</option>
            <option value="text">Text</option>
          </select>
        </Field>
        {resultType === 'number' && (
          <Field label="Decimals (optional)">
            <Input
              value={decimals}
              onChange={(e) => setDecimals(e.target.value)}
              placeholder="auto"
              inputMode="numeric"
              className="h-8 text-[13px] rounded-sm"
            />
          </Field>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} className="h-8 rounded-sm text-[13px]">
          Cancel
        </Button>
        <Button
          disabled={!valid}
          onClick={handleSubmit}
          className="h-8 rounded-sm text-[13px] bg-[#0078d4] hover:bg-[#106ebe] text-white"
        >
          Add column
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-stone-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
